import { useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { AppContext } from "../../../context/appContext";
import { GLBalancePostingWrapper } from "./styled";
import { BaseAlertModal } from "../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../assets";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Typography } from "@mui/material";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { BaseOption } from "../../../components/form/option/styled";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllChartOfAccountService } from "../../../utils/api/accounting/chartofaccount/retrieveAllChartOfAccount";
import Cookies from "universal-cookie";
import { retrieveAllBranchesService } from "../../../utils/api/configuration/branch/retrieveAllBranches";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseButton } from "../../../components/button/styled";
import { postGLBalanceService } from "../../../utils/api/configuration/glbalanceposting/postGLBalance";
import { retrieveAllGLBalancesService } from "../../../utils/api/configuration/glbalanceposting/retrieveAllGLBalances";

export const GLBalancePosting = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const entryTypes = [
        { name: "Debit", abbreviation: "dr" },
        { name: "Credit", abbreviation: "cr" }
    ];

    const navigate = useNavigate();
    const { setActiveNavItem } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [formDetails, setFormDetails] = useState({
        glReference: " ",
        domiciledBranch: " ",
        entryType: " ",
        amount: "",
        description: "",
    });

    useEffect(() => {
        setActiveNavItem("Configuration");
    }, [setActiveNavItem]);

    const { data: glAccounts } = useQuery({
        queryKey: ["glAccounts", TOKEN],
        queryFn: async () => {
            const response = await retrieveAllChartOfAccountService(TOKEN);
            return response?.data?.filter((gl: Record<string, any>) => gl.glAccountType === "GL Account") ?? [];
        },
        enabled: !!TOKEN,
    });

    const { data: branches } = useQuery({
        queryKey: ['branches', TOKEN, formDetails.glReference],
        queryFn: async () => {
            const [allBranches, glBalances] = await Promise.all([
                retrieveAllBranchesService(TOKEN),
                retrieveAllGLBalancesService(TOKEN),
            ]);
            const filteredBranches = allBranches?.data?.filter((branch: Record<string, any>) => {
                return !glBalances.some((glBalance: Record<string, any>) =>
                    glBalance.glReference === formDetails.glReference &&
                    glBalance.branch === branch.id
                );
            });
            return filteredBranches;
        },
        enabled: !!TOKEN && !!formDetails.glReference,
    });

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value,
            ...(name === "glReference" && { domiciledBranch: " " })
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const response = await postGLBalanceService(TOKEN, formDetails);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError(`Failed to post GL balance. Please check credentials and try again.`);
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to post GL balance. ${error.message}`);
            console.error(`Failed to post GL balance:`, error);
        }
    };

    return (
        <AppLayout
            pageId={"GL-Balance-Posting"}
            pageTitle={"GL Balance Posting"}
        >
            <GLBalancePostingWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title={"GL Balance Posting Successful"}
                    message="GL Balance has been successfully posted."
                    callToAction="OK"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required information to post an opening balance for a GL account.
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    GL Account
                                </BaseLabel>
                                <BaseSelect
                                    name="glReference"
                                    fontweight={400}
                                    value={formDetails.glReference}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value=" "
                                    >
                                        Select GL Account
                                    </BaseOption>
                                    {glAccounts?.map((glAccount: Record<string, any>, index: number) => (
                                        <BaseOption
                                            key={index}
                                            value={glAccount.id}
                                        >
                                            {glAccount.glName?.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                        </BaseOption>
                                    ))}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Branch
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="domiciledBranch"
                                    fontweight={400}
                                    value={formDetails.domiciledBranch}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select Branch
                                    </BaseOption>
                                    {branches?.map((branch: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={branch.id}
                                            >
                                                {branch.name}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    DR/CR
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="entryType"
                                    fontweight={400}
                                    value={formDetails.entryType}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select Entry Type
                                    </BaseOption>
                                    {entryTypes?.map((type: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={type.abbreviation}
                                            >
                                                {`${type.name.charAt(0).toUpperCase() + type.name.slice(1)} (${type.abbreviation.toUpperCase()})`}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Amount
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="amount"
                                    value={formDetails.amount}
                                    placeholder="Enter Amount"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Description
                                </BaseLabel>
                                <BaseInput
                                    required
                                    multiline
                                    minRows={4}
                                    name="description"
                                    value={formDetails.description}
                                    placeholder="Enter description."
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Submit
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </GLBalancePostingWrapper>
        </AppLayout>
    )
}